/*
 * START_MODULE_CONTRACT
 *   PURPOSE: Sticky top nav with main links, admin section, mobile hamburger, profile cluster, and language switcher
 *   PROPS:   extends HTML nav attributes
 *   STATE:   menuOpen (mobile hamburger toggle), syncs with pathname
 *   DEPENDS: M-BTN (Button), M-LNG (LangSwitch), M-CN (cn), next-intl, @phosphor-icons/react
 *   EMITS:   navigation via Link
 *   ACCESSIBILITY: aria-label on nav, mobile button has aria-label, focusable links
 *   LINKS:   M-TNV
 * END_MODULE_CONTRACT
 *
 * CHANGE_SUMMARY
 *   [2026-06-12] Added GRACE MODULE_CONTRACT and semantic blocks — no behavior change
 */

"use client"

import { forwardRef, useState, useEffect } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { useTranslations, useLocale } from "next-intl"
import { cn } from "@/lib/cn"
import { UserCircle, GearSix, Bell, List, X } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { LangSwitch } from "@/components/layout/lang-switch"
import { useAuth } from "@/hooks/use-auth"

// --- TypeDefinitions ---
interface NavItem {
  label: string
  href: string
}

// --- TopNavComponent ---
const TopNav = forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  ({ className, ...props }, ref) => {
    // --- TranslationsAndRouting ---
    const t = useTranslations("nav")
    const tc = useTranslations("common")
    const locale = useLocale()
    const pathname = usePathname()
    const [menuOpen, setMenuOpen] = useState(false)
    const { user } = useAuth()
    const isAdmin = user?.role === "ADMIN"

    // --- MobileMenuEffects ---
    useEffect(() => {
      setMenuOpen(false)
    }, [pathname])

    useEffect(() => {
      document.body.style.overflow = menuOpen ? "hidden" : ""
      return () => { document.body.style.overflow = "" }
    }, [menuOpen])

    // --- NavigationConfig ---
    const mainNav: NavItem[] = [
      { label: t("dashboard"), href: "/dashboard" },
      { label: t("agents"), href: "/agents" },
      { label: t("aiUsage"), href: "/ai-usage" },
      { label: t("terminal"), href: "/terminal" },
      { label: t("telegram"), href: "/telegram" },
      { label: t("backups"), href: "/backups" },
      { label: t("logs"), href: "/logs" },
      { label: t("subscription"), href: "/subscriptions" },
    ]

    const userNav: NavItem[] = [
      { label: t("dashboard"), href: "/dashboard" },
      { label: t("subscription"), href: "/subscriptions" },
    ]

    const adminNav: NavItem[] = [
      { label: t("admin_agents"), href: "/admin/agents" },
      { label: t("admin_nodes"), href: "/admin/nodes" },
      { label: t("admin_users"), href: "/admin/users" },
      { label: t("admin_plans"), href: "/admin/plans" },
      { label: t("admin_infrastructure"), href: "/admin/infrastructure" },
    ]

    // --- ActiveRouteCheck ---
    const isActive = (href: string) => {
      const cleanPath = pathname.replace(/^\/[a-z]{2}/, "")
      return cleanPath === href || cleanPath.startsWith(href + "/")
    }

    // --- Render ---
    return (
      <>
        <header
          ref={ref}
          className={cn(
            "sticky top-0 z-50 flex h-navbar items-center border-b border-hairline-dark bg-canvas-dark px-4 md:px-6",
            className,
          )}
          {...props}
        >
          <Link href="/dashboard" className="mr-4 flex shrink-0 items-center gap-2 md:mr-6">
            <span className="text-lg font-bold text-primary">{tc("brand")}</span>
          </Link>

          <nav className="hidden items-center gap-0.5 overflow-x-auto lg:flex" aria-label="Main navigation">
            {isAdmin ? (
              <>
                {mainNav.map((item) => (
                  <Link
                    key={item.href}
                    href={`/${locale}${item.href}`}
                    className={cn(
                      "rounded-md px-2.5 py-2 text-xs font-medium transition-colors duration-150 whitespace-nowrap xl:px-3 xl:text-sm",
                      isActive(item.href)
                        ? "bg-surface-card-dark text-on-dark"
                        : "text-muted hover:bg-surface-card-dark hover:text-on-dark",
                    )}
                  >
                    {item.label}
                  </Link>
                ))}
                <span className="mx-1.5 h-5 w-px bg-hairline-dark" aria-hidden="true" />
                {adminNav.map((item) => (
                  <Link
                    key={item.href}
                    href={`/${locale}${item.href}`}
                    className={cn(
                      "rounded-md px-2.5 py-2 text-xs font-medium transition-colors duration-150 whitespace-nowrap xl:px-3 xl:text-sm",
                      isActive(item.href)
                        ? "bg-surface-card-dark text-primary"
                        : "text-muted hover:bg-surface-card-dark hover:text-on-dark",
                    )}
                  >
                    {item.label}
                  </Link>
                ))}
              </>
            ) : (
              <>
                {userNav.map((item) => (
                  <Link
                    key={item.href}
                    href={`/${locale}${item.href}`}
                    className={cn(
                      "rounded-md px-2.5 py-2 text-xs font-medium transition-colors duration-150 whitespace-nowrap xl:px-3 xl:text-sm",
                      isActive(item.href)
                        ? "bg-surface-card-dark text-on-dark"
                        : "text-muted hover:bg-surface-card-dark hover:text-on-dark",
                    )}
                  >
                    {item.label}
                  </Link>
                ))}
              </>
            )}
          </nav>

          <div className="ml-auto flex items-center gap-1">
            <div className="hidden md:flex md:items-center md:gap-1">
              <LangSwitch />
              <Link href={`/${locale}/profile`}>
                <Button variant="ghost" size="sm">
                  <UserCircle weight="regular" aria-hidden="true" />
                </Button>
              </Link>
              <Link href={`/${locale}/settings`}>
                <Button variant="ghost" size="sm">
                  <GearSix weight="regular" aria-hidden="true" />
                </Button>
              </Link>
              <Button variant="ghost" size="sm" aria-label="Notifications">
                <Bell weight="regular" aria-hidden="true" />
              </Button>
            </div>

            <button
              className="flex h-9 w-9 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-card-dark hover:text-on-dark lg:hidden"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
            >
              {menuOpen ? <X weight="regular" className="h-5 w-5" /> : <List weight="regular" className="h-5 w-5" />}
            </button>
          </div>
        </header>

        {menuOpen && (
          <div className="fixed inset-0 top-navbar z-40 flex flex-col bg-canvas-dark lg:hidden">
            <nav className="flex-1 overflow-y-auto px-4 py-4 space-y-1" aria-label="Mobile navigation">
              {isAdmin ? (
                <>
                  {mainNav.map((item) => (
                    <Link
                      key={item.href}
                      href={`/${locale}${item.href}`}
                      className={cn(
                        "flex h-11 items-center rounded-md px-3 text-sm font-medium transition-colors",
                        isActive(item.href)
                          ? "bg-surface-card-dark text-on-dark"
                          : "text-muted hover:bg-surface-card-dark hover:text-on-dark",
                      )}
                    >
                      {item.label}
                    </Link>
                  ))}
                  <div className="my-2 border-t border-hairline-dark" />
                  <p className="px-3 py-1 text-xs font-semibold uppercase text-muted">Admin</p>
                  {adminNav.map((item) => (
                    <Link
                      key={item.href}
                      href={`/${locale}${item.href}`}
                      className={cn(
                        "flex h-11 items-center rounded-md px-3 text-sm font-medium transition-colors",
                        isActive(item.href)
                          ? "bg-surface-card-dark text-primary"
                          : "text-muted hover:bg-surface-card-dark hover:text-on-dark",
                      )}
                    >
                      {item.label}
                    </Link>
                  ))}
                </>
              ) : (
                <>
                  {userNav.map((item) => (
                    <Link
                      key={item.href}
                      href={`/${locale}${item.href}`}
                      className={cn(
                        "flex h-11 items-center rounded-md px-3 text-sm font-medium transition-colors",
                        isActive(item.href)
                          ? "bg-surface-card-dark text-on-dark"
                          : "text-muted hover:bg-surface-card-dark hover:text-on-dark",
                      )}
                    >
                      {item.label}
                    </Link>
                  ))}
                </>
              )}
            </nav>

            <div className="border-t border-hairline-dark px-4 py-3 flex items-center justify-between">
              <LangSwitch />
              <div className="flex gap-1">
                <Link href={`/${locale}/profile`}>
                  <Button variant="ghost" size="sm"><UserCircle weight="regular" aria-hidden="true" /></Button>
                </Link>
                <Link href={`/${locale}/settings`}>
                  <Button variant="ghost" size="sm"><GearSix weight="regular" aria-hidden="true" /></Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </>
    )
  },
)
TopNav.displayName = "TopNav"

export { TopNav }
export type { NavItem }
